import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";
import {
  ArrowLeft, ExternalLink, Copy, Check, Mail, Share2, ChevronDown,
  ClipboardList, Compass, Sparkles, FileText, BookOpen, Megaphone, Heart, Gift,
  Plus, Pencil, Trash2, Save, X, Loader2, Search as SearchIcon,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CJ_AVATAR_URL } from "../lib/constants";
import { toast } from "sonner";

const ICON_MAP = {
  Share2, ClipboardList, Compass, Sparkles, FileText, ExternalLink,
  Mail, BookOpen, Megaphone, Heart, Gift,
};
const ICON_OPTIONS = Object.keys(ICON_MAP);
const COLOR_OPTIONS = ["#f68b1f", "#0091be", "#7ac143", "#ffd400", "#083050"];

export default function Resources() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState("");
  const [copied, setCopied] = useState("");
  const [editing, setEditing] = useState(null); // resource object, "new", or null
  const [search, setSearch] = useState("");

  // Fuzzy + acronym-aware search — reps often type shortcuts like "cca" for
  // Culture Clarity Assessment or search a bit of the URL. Fuse handles the
  // typo tolerance and acronym match.
  const indexed = useMemo(() => items.map((it) => ({
    ...it,
    acronym: (it.title || "")
      .split(/\s+/)
      .map((w) => w.replace(/[^A-Za-z]/g, "")[0] || "")
      .join("")
      .toLowerCase(),
  })), [items]);

  const fuse = useMemo(() => new Fuse(indexed, {
    keys: [
      { name: "title", weight: 3 },
      { name: "acronym", weight: 3 },
      { name: "blurb", weight: 1 },
      { name: "share_link", weight: 0.5 },
      { name: "email_template", weight: 0.5 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
    minMatchCharLength: 2,
  }), [indexed]);

  const filteredItems = useMemo(() => {
    const q = search.trim();
    if (!q) return items;
    return fuse.search(q).map((r) => r.item);
  }, [items, search, fuse]);

  const reload = () => {
    setLoading(true);
    return api.get("/resources")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(""), 2200);
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/resources/${r.id}`);
      toast.success("Resource removed.");
      await reload();
    } catch (err) {
      toast.error("Couldn't delete", { description: err?.response?.data?.detail || err?.message });
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <Link to="/" data-testid="resources-back" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink">
        <ArrowLeft size={14} /> Back to Hub
      </Link>

      <div className="mt-4 flex items-start gap-4 flex-wrap">
        <img src={CJ_AVATAR_URL} alt="CJ" className="h-16 w-16 rounded-full object-cover shadow-md ring-2 ring-white shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <div className="tc-eyebrow text-yellow-700">Resources</div>
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">Everything you need, in one place.</h1>
          <p className="text-slate-500 mt-1.5 text-sm max-w-xl">Click a card to expand. Each one has the share link and, where it helps, a ready-to-paste email.</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setEditing("new")}
            data-testid="resources-add"
            className="h-11 px-4 rounded-2xl text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
            style={{ background: "#ffd400", color: "#083050" }}
          >
            <Plus size={16} /> Add resource
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-10 text-center text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center text-slate-500">
          <FileText size={32} className="mx-auto opacity-40" />
          <div className="mt-3 font-bold">No resources yet.</div>
          {canEdit && <p className="text-sm mt-1">Click &ldquo;Add resource&rdquo; to create your first.</p>}
        </div>
      ) : (
        <>
          {/* Search — fuzzy + acronym-aware. Hidden until there's more than
              a handful of resources so the UI stays clean for small lists. */}
          <div className="mt-6 relative">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search resources — title, description, or acronym (e.g. CCA)"
              data-testid="resources-search"
              className="w-full h-11 pl-9 pr-9 rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none text-sm bg-white"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="resources-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center hover:bg-slate-100 text-slate-400"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {filteredItems.length === 0 ? (
            <div className="mt-10 text-center text-slate-500" data-testid="resources-no-results">
              <FileText size={28} className="mx-auto opacity-40" />
              <div className="mt-3 font-bold text-tc-ink">No matches for &ldquo;{search}&rdquo;</div>
              <button
                onClick={() => setSearch("")}
                className="text-sm text-orange-600 font-bold mt-2 hover:underline"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3" data-testid="resources-accordion">
              {filteredItems.map((r, idx) => (
                <ResourceCard
                  key={r.id}
                  r={r}
                  open={openKey === r.id}
              onToggle={() => setOpenKey((cur) => (cur === r.id ? "" : r.id))}
              copied={copied}
              onCopy={copy}
              canEdit={canEdit}
              onEdit={() => setEditing(r)}
              onDelete={() => remove(r)}
              delay={idx * 0.04}
            />
          ))}
              </div>
            )}
          </>
      )}

      {editing && (
        <ResourceEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}
    </div>
  );
}

function ResourceCard({ r, open, onToggle, copied, onCopy, canEdit, onEdit, onDelete, delay }) {
  const Icon = ICON_MAP[r.icon] || ExternalLink;
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
      className="bg-white rounded-2xl border-2 overflow-hidden"
      style={{ borderColor: open ? r.color : "#e2e8f0" }}
      data-testid={`resource-card-${r.id}`}
    >
      <div className="h-1.5 w-full" style={{ backgroundColor: r.color }} />
      <div className="flex items-center gap-1 pr-3">
        <button
          onClick={onToggle}
          data-testid={`resource-toggle-${r.id}`}
          aria-expanded={open}
          className="flex-1 text-left p-4 sm:p-5 flex items-center gap-4 hover:bg-slate-50/60 transition-colors"
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${r.color}1f`, color: r.color }}
          >
            <Icon size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-tc-ink leading-tight">{r.title}</div>
            {r.blurb && <div className="text-xs sm:text-sm text-slate-500 mt-0.5 line-clamp-2">{r.blurb}</div>}
          </div>
          <ChevronDown
            size={20}
            className="text-slate-400 shrink-0 transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
        {canEdit && (
          <div className="flex items-center gap-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              data-testid={`resource-edit-${r.id}`}
              title="Edit"
              className="w-9 h-9 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-tc-ink"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              data-testid={`resource-delete-${r.id}`}
              title="Delete"
              className="w-9 h-9 rounded-lg hover:bg-red-50 inline-flex items-center justify-center text-slate-400 hover:text-red-500"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-100"
          >
            <div className="p-4 sm:p-5 space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <div
                  className="flex-1 min-w-[220px] flex items-center gap-2 px-3 sm:px-4 h-11 rounded-xl bg-slate-50 border border-slate-200 font-mono text-xs sm:text-sm text-tc-ink truncate"
                  data-testid={`resource-url-${r.id}`}
                  title={r.url}
                >
                  {r.url}
                </div>
                <button
                  onClick={() => onCopy(r.url, `${r.id}-url`)}
                  data-testid={`resource-copy-url-${r.id}`}
                  className="h-11 px-4 rounded-xl font-bold text-sm flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all"
                  style={{ backgroundColor: r.color, color: textOn(r.color) }}
                >
                  {copied === `${r.id}-url` ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`resource-open-${r.id}`}
                  className="h-11 px-4 rounded-xl bg-white border border-slate-200 text-tc-ink font-bold text-sm flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all"
                >
                  <ExternalLink size={14} /> Open
                </a>
              </div>

              {r.email && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Mail size={16} style={{ color: r.color }} />
                      <div className="font-bold text-tc-ink text-sm">Quick email template</div>
                    </div>
                    <button
                      onClick={() => onCopy(r.email, `${r.id}-email`)}
                      data-testid={`resource-copy-email-${r.id}`}
                      className="h-9 px-3 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-colors"
                      style={{ backgroundColor: `${r.color}26`, color: r.color }}
                    >
                      {copied === `${r.id}-email` ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy email</>}
                    </button>
                  </div>
                  <pre
                    className="mt-3 p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs sm:text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed"
                    data-testid={`resource-email-${r.id}`}
                  >
                    {r.email}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

function ResourceEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [draft, setDraft] = useState(() => ({
    title: initial?.title || "",
    blurb: initial?.blurb || "",
    url: initial?.url || "",
    color: initial?.color || "#0091be",
    icon: initial?.icon || "ExternalLink",
    email: initial?.email || "",
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Title is required.");
    if (!draft.url.trim()) return toast.error("URL is required.");
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        blurb: draft.blurb.trim(),
        url: draft.url.trim(),
        color: draft.color,
        icon: draft.icon,
        email: draft.email.trim() || null,
      };
      if (isNew) {
        await api.post("/resources", payload);
        toast.success("Resource added.");
      } else {
        await api.put(`/resources/${initial.id}`, payload);
        toast.success("Resource updated.");
      }
      await onSaved();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setSaving(false);
    }
  };

  const Icon = ICON_MAP[draft.icon] || ExternalLink;
  const INPUT = "w-full h-11 px-3 rounded-xl bg-white border-2 border-slate-200 focus:border-[#ffd400] outline-none text-sm";

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="resource-editor-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 180, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        data-testid="resource-editor"
      >
        <div className="h-2 w-full" style={{ backgroundColor: draft.color }} />
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl text-tc-ink">{isNew ? "Add resource" : "Edit resource"}</h2>
            <button onClick={onClose} data-testid="resource-editor-close" className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
              <X size={18} />
            </button>
          </div>

          {/* Live preview */}
          <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${draft.color}1f`, color: draft.color }}
            >
              <Icon size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm text-tc-ink truncate">{draft.title || "Resource title"}</div>
              <div className="text-xs text-slate-500 truncate">{draft.blurb || "Short description shows here…"}</div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <Label>Title</Label>
              <input
                value={draft.title}
                onChange={(e) => set("title", e.target.value)}
                data-testid="resource-editor-title"
                className={INPUT}
                placeholder="e.g. Culture Reality Check"
              />
            </div>
            <div>
              <Label>Short description (optional)</Label>
              <input
                value={draft.blurb}
                onChange={(e) => set("blurb", e.target.value)}
                data-testid="resource-editor-blurb"
                className={INPUT}
                placeholder="One sentence shown under the title."
              />
            </div>
            <div>
              <Label>Link URL</Label>
              <input
                value={draft.url}
                onChange={(e) => set("url", e.target.value)}
                data-testid="resource-editor-url"
                className={INPUT}
                placeholder="https://…"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Color</Label>
                <div className="flex items-center gap-2 flex-wrap" data-testid="resource-editor-colors">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => set("color", c)}
                      data-testid={`resource-editor-color-${c.slice(1)}`}
                      className="w-9 h-9 rounded-lg border-2 transition-transform hover:scale-105"
                      style={{ backgroundColor: c, borderColor: draft.color === c ? "#083050" : "transparent" }}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label>Icon</Label>
                <select
                  value={draft.icon}
                  onChange={(e) => set("icon", e.target.value)}
                  data-testid="resource-editor-icon"
                  className={INPUT}
                >
                  {ICON_OPTIONS.map((i) => <option key={i}>{i}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label>Email template (optional)</Label>
              <textarea
                value={draft.email}
                onChange={(e) => set("email", e.target.value)}
                data-testid="resource-editor-email"
                rows={6}
                className="w-full px-3 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-[#ffd400] outline-none text-sm font-sans"
                placeholder="Paste an email here so the team can copy it with one click. Leave blank if not needed."
              />
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button onClick={onClose} className="h-11 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="resource-editor-save"
              className="h-11 px-5 rounded-xl font-bold text-sm flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-60"
              style={{ background: draft.color, color: textOn(draft.color) }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? "Add resource" : "Save changes"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Label({ children }) {
  return <div className="tc-eyebrow text-slate-500 mb-1.5">{children}</div>;
}

function textOn(hex) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#083050" : "#FFFFFF";
}
